import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { F6252Service } from './f-6252.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f-6252',
    templateUrl: './f-6252.component.html',
    styleUrls: ['./f-6252.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F6252Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  minimise = false;
  isSaveClicked = false;
  activeTab = 1;

  // Modal properties
  modalTitle = "Form 6252";
  
  // Entity data properties (custom structure from original)
  entityInfo: any = {};

  // Loading states
  showFormLoader = true;
  crudLoading = false;
  deletingOccurrence: any = null;

  // Data from AngularJS
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  appService: any = { USER_SETTINGS: {}, SERVER_CONFIG: {} };

  private readonly subs = new Subscription();
  
  // Component-specific data
  f6252Header: any = {};
  f6252List: any[] = [];
  f6252List1: any[] = [];
  tableHeader: any[] = [];
  f6252RowData: any[] = [];
  f6252FormData: any[] = [];
  f6252OriginalData: any[] = [];
  f6252FinalData: any[] = [];
  
  // Display flags
  showAttributes = false;
  initialLoad = false;
  
  // Lists
  outerList: any[] = [];
  innerList: any[] = [];
  radioLines: any[] = [];
  currentOutterList: any[] = [];
  filteredOuterList: any[] = [];
  
  // LOV data
  formLOV: any[] = [];
  formCalcData: any[] = [];
  
  // User settings
  logged_in_user: string = '';
  curentyear: string = '';
  
  // Calendar
  calendar: any = {
    opened: false
  };
  
  // Current occurrence
  currentOccurrence: number = 1;
  
  // Date options
  dateOptions: any = {
    dateDisabled: false,
    formatYear: 'yyyy',
    startingDay: 1,
    showWeeks: false
  };

  constructor(
    private f6252Service: F6252Service,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.USER_SETTINGS = this.appService.USER_SETTINGS;
    this.SERVER_CONFIG = this.appService.SERVER_CONFIG;
    this.cdr.markForCheck();
  }

  private isInitialized = false;

  init() {
    try {
      // Check if all required data is available before initializing
      if (!this.globalService || Object.keys(this.globalService).length === 0 ||
          !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url ||
          !this.rowData || Object.keys(this.rowData).length === 0) {
        return;
      }
      
      // Check if already initialized to prevent multiple calls
      if (this.isInitialized) {
        return;
      }
      
      this.isInitialized = true;
      this.setupEntityData();
      this.curentyear = this.filterParams.tax_year || this.globalService.globalParams?.tax_year;
      this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
      
      // Load LOV and data
      this.fetchLOV();
      this.fetchModalCalcData();
    } catch (e) {
      console.error('Error in F6252 init:', e);
    }
  }

  private setupEntityData() {
    // Custom entity structure from original component
    this.entityInfo = {
      leid: this.rowData.LEID,
      leName: this.rowData.LE_NAME,
      taxYearBegin: this.rowData.TAX_YEAR_BEGIN,
      taxYearEnd: this.rowData.TAX_YEAR_END,
      meCode: this.rowData.ME_CODE,
      meName: this.rowData.ME_NAME,
      cdrNo: this.rowData.CDR_NO
    };
  }

  fetchLOV() {
    const specificParams = {
      action_code: 'sjh60s',
      sso_id: this.logged_in_user,
      tax_year: this.curentyear,
      scenario: this.globalService.globalParams?.scenario,
      jcd_key: this.globalService.globalParams?.jcd_key,
      form_no: '6252'
    };
    
    this.f6252Service.fetchLOV(
      this.GENERAL_CONFIG.base_url, 
      specificParams,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          this.formLOV = data.jsonObject;
          this.getF6252Data();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error fetching LOV:', error);
        this.gtwSnackbarService.openSnackBar('Error loading form data', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  fetchModalCalcData() {
    const specificParams = {
      action_code: '1ubsuu',
      tax_year: this.curentyear,
      scenario: this.globalService.globalParams?.scenario,
      jcd_key: this.globalService.globalParams?.jcd_key,
      form_no: '6252',
      schedule_name: 'N/A',
      part_no: '',
      section_name: 'N/A'
    };
    
    this.f6252Service.fetchModalCalcData(
      this.GENERAL_CONFIG.base_url, 
      specificParams,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          this.formCalcData = data.jsonObject;
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error fetching calc data:', error);
        this.cdr.markForCheck();
      }
    );
  }

  getF6252Data() {
    this.f6252Service.fetchF6252Details(
      this.rowData, 
      this.logged_in_user,
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.f6252Header = data.jsonObject && data.jsonObject[0] ? data.jsonObject[0] : {};
        this.modalTitle = this.f6252Header?.ATTRIB_DESCRIPTION || "Form 6252";
        
        this.f6252List1 = data.jsonObject ? data.jsonObject.slice(1) : [];
        this.tableHeader = this.f6252List1.filter((obj: any) => obj.ROW_TYPE === "TH");
        
        this.f6252List = this.f6252List1.filter((obj: any) => obj.ROW_TYPE !== 'TH');
        
        // Group by IS_MULTI
        const groupedByMulti = _.groupBy(this.f6252List, 'IS_MULTI');
        
        this.f6252RowData = Object.keys(groupedByMulti).map(key => {
          return {
            IS_MULTI: groupedByMulti[key][0].IS_MULTI,
            Row_data: groupedByMulti[key]
          };
        });
        
        // Process each IS_MULTI group
        for (let i = 0; i < this.f6252RowData.length; i++) {
          // Group by TRANS_OCCURRENCE
          const groupedByOcc = _.groupBy(this.f6252RowData[i].Row_data, 'TRANS_OCCURRENCE');
          
          const occArray = Object.keys(groupedByOcc).map(k => {
            return {
              Line_Type: "INITIAL_ROW",
              TRANS_OCCURRENCE: groupedByOcc[k][0].TRANS_OCCURRENCE,
              FORM_INPUT_DATA: groupedByOcc[k][0].FORM_INPUT_DATA,
              Is_Multi: groupedByOcc[k][0].IS_MULTI,
              Line_no: groupedByOcc[k][0].LINE_NO,
              IS_EDITABLE: groupedByOcc[k][0].IS_EDITABLE,
              IS_EDITTED: "N",
              IS_CURRENT_CHANGED_ROW: "N",
              Trans_details_key: groupedByOcc[k][0].TRANS_DETAILS_KEY,
              Line_data: groupedByOcc[k],
              DELETE_ROWS: []
            };
          });
          
          this.f6252RowData[i].Row_data = occArray;
          
          if (this.f6252RowData[i].IS_MULTI != 'Y') {
            // Process non-multi rows
            for (let j = 0; j < this.f6252RowData[i].Row_data.length; j++) {
              
              if (this.f6252RowData[i].Row_data[j].Line_data[0].TRANS_OCCURRENCE == null) {
                this.f6252RowData[i].Row_data[j].TRANS_OCCURRENCE = 1;
              }
              
              // Group by LINE_ORDER
              const groupedByOrder = _.groupBy(this.f6252RowData[i].Row_data[j].Line_data, 'LINE_ORDER');
              
              const lineArray = Object.keys(groupedByOrder).map(k => {
                return {
                  ROW_TYPE: groupedByOrder[k][0].ROW_TYPE,
                  DELETE_ROWS: [],
                  Line_data_occ: groupedByOrder[k]
                };
              });
              
              // Find line 29(a)(ii) - date field
              const line29aDate = lineArray.filter((o: any) => {
                for (let d = 0; d < o.Line_data_occ.length; d++) {
                  if (o.Line_data_occ[d].LINE_NO == '29(a)(ii)') {
                    return true;
                  }
                }
                return false;
              });
              
              // Find line 29(a) - checkbox
              const line29a = lineArray.filter((o: any) => {
                for (let d = 0; d < o.Line_data_occ.length; d++) {
                  if (o.Line_data_occ[d].LINE_NO == '29(a)') {
                    // If checked
                    if (o.Line_data_occ[d].ATTRIB_VALUE == 'true') {
                      o.Line_data_occ[d]['conditionalField'] = false;
                      if (line29aDate && line29aDate.length > 0 && line29aDate[0].Line_data_occ[0].ATTRIB_VALUE) {
                        // Store as Date object (like original AngularJS) - will serialize to ISO string
                        const formatDate = this.formatDateString(line29aDate[0].Line_data_occ[0].ATTRIB_VALUE);
                        o.Line_data_occ[d]['line29DateVal'] = new Date(formatDate);
                      }
                      o.Line_data_occ[d]['isOpen'] = false;
                    } else {
                      o.Line_data_occ[d]['conditionalField'] = true;
                      o.Line_data_occ[d]['line29DateVal'] = null;
                    }
                    return true;
                  }
                }
                return false;
              });
              
              this.f6252RowData[i].Row_data[j].Line_data = _.cloneDeep(lineArray);
            }
          } else {
            // Process multi rows (properties)
            let headerRow = _.cloneDeep(occArray[0]);
            headerRow.Line_Type = "HEADER_ROW";
            headerRow.TRANS_OCCURRENCE = "";
            headerRow.Trans_details_key = "";
            headerRow.IS_EDITABLE = "Y";
            headerRow.Is_Multi = "Y";
            
            for (let j = 0; j < headerRow.Line_data.length; j++) {
              headerRow.Line_data[j].ATTRIB_VALUE = headerRow.Line_data[j].ATTRIB_DESCRIPTION;
              this.f6252RowData[i]['line_No'] = headerRow.Line_data[j].LINE_NO;
            }
            
            // Process existing data rows
            for (let m = 0; m < this.f6252RowData[i].Row_data.length; m++) {
              if (this.f6252RowData[i].Row_data[m].FORM_INPUT_DATA == 'Y') {
                this.f6252RowData[i].Row_data[m].Line_Type = "DATA_ROW";
                
                for (let n = 0; n < this.f6252RowData[i].Row_data[m].Line_data.length; n++) {
                  if (this.f6252RowData[i].Row_data[m].Line_data[n].ATTRIB_SHORT_CODE == "PROPERTY_DESC_1") {
                    const selectedProp = this.formLOV.filter((o: any) => 
                      o.CODE_NAME == this.f6252RowData[i].Row_data[m].Line_data[n].ATTRIB_VALUE
                    );
                    if (selectedProp && selectedProp.length > 0) {
                      this.f6252RowData[i].Row_data[m].Line_data[n].ATTRIB_VALUE = selectedProp[0].CODE_DESC;
                    }
                  }
                }
              }
            }
            
            this.f6252RowData[i].Row_data.unshift(headerRow, {
              Line_Type: "SUB_ROW",
              Line_no: this.f6252RowData[i]['line_No'],
              Line_data: []
            });
          }
        }
        
        this.f6252FormData = this.f6252RowData;
        
        // Build outerList, innerList, radioLines
        this.outerList = [];
        this.innerList = [];
        this.radioLines = [];
        
        for (let i = 0; i < this.f6252FormData.length; i++) {
          if (this.f6252FormData[i].IS_MULTI == 'N') {
            for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
              for (let m = 0; m < this.f6252FormData[i].Row_data[j].Line_data.length; m++) {
                if (this.f6252FormData[i].Row_data[j].Line_data[m].ROW_TYPE != 'FH') {
                  for (let n = 0; n < this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ.length; n++) {
                    if (this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n].FORM_LINE_KEY != null) {
                      this.innerList.push(this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n]);
                      
                      // radio
                      if (this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n].EDIT_TYPE == "radio") {
                        this.radioLines.push(this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n]);
                      }
                      
                      let tempCheck1: any[] = [];
                      if (this.outerList != null && this.outerList.length > 0) {
                        tempCheck1 = this.outerList.filter((obj: any) => 
                          obj.TRANS_OCCURRENCE === this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n].TRANS_OCCURRENCE &&
                          obj.FORM_LINE_KEY === this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n].FORM_LINE_KEY &&
                          obj.IS_FORM_DISPLAY === 'Y'
                        );
                      }
                      
                      if (tempCheck1 == undefined || tempCheck1.length == 0) {
                        if (this.initialLoad) {
                          this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n].TRANS_OCCURRENCE = 1;
                        }
                        this.outerList.push(this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n]);
                      }
                    }
                  }
                }
              }
            }
          }
        }
        
        this.currentOutterList = this.outerList.filter((o: any) => o.TRANS_OCCURRENCE == "1");
        
        // Run calcFinalAmounts for all TRANS_OCCURRENCE to calculate formulas on initial load
        // Get all unique TRANS_OCCURRENCE values
        const uniqueOccurrences = _.uniq(this.outerList.map((o: any) => o.TRANS_OCCURRENCE));
        uniqueOccurrences.forEach((occurrence: any) => {
          this.calcFinalAmounts(occurrence);
        });
        
        this.f6252OriginalData = _.cloneDeep(this.f6252FormData);
        
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching F6252 details:', error);
        this.gtwSnackbarService.openSnackBar('Error loading form details', 'gtw-snackbar--red');
        this.showFormLoader = false;
        this.cdr.markForCheck();
      }
    );
  }

  formatDateString(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const month = ('0' + (d.getMonth() + 1)).slice(-2);
      const day = ('0' + d.getDate()).slice(-2);
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (e) {
      return dateStr;
    }
  }

  checkBoxHandler(lineKey: any, attribKey: any, attribCol: any, occurrence: any) {
    const checkedVal = this.radioLines.filter((obj: any) => 
      obj.FORM_LINE_KEY === lineKey && 
      obj.ATTRIB_COL === attribCol && 
      obj.TRANS_OCCURRENCE === occurrence && 
      obj.IS_FORM_DISPLAY === 'Y'
    );

    _.forEach(checkedVal, (el: any, index: number) => {
      if (el.LINE_ATTRIB_KEY == attribKey) {
        el.ATTRIB_VALUE = 'true';
        el.IS_CHANGED_FLAG = 'Y';
      } else {
        el.ATTRIB_VALUE = 'false';
        el.IS_CHANGED_FLAG = 'Y';
      }
    });
  }

  openCalendar(row: any) {
    row['isOpen'] = true;
  }

  openCalendar1(row: any) {
    row['isOpen'] = true;
  }

  formatDate(row: any) {
    if (row.ATTRIB_VALUE) {
      row["taxyearBE"] = this.formatDateString(row.ATTRIB_VALUE);
    }
  }

  reset() {
    this.getF6252Data();
    this.showAttributes = false;
  }

  save(occurrence: number, type: string) {
    let finalData: any[] = [];
    let clob_settings: any[] = [];
    
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar("Please wait while we save this request", 'gtw-snackbar--yellow');
      return;
    }
    
    this.f6252FinalData = _.cloneDeep(this.f6252FormData);
    
    if (_.isEqual(this.f6252FormData, this.f6252OriginalData)) {
      this.cancel();
      return;
    } else {
      this.updateFormDataValues();
      
      const returnValue = this.validateFormValues(this.currentOccurrence);
      if (returnValue == 1) {
        this.gtwSnackbarService.openSnackBar('Please correct the validation errors in the marked fields before saving.', 'gtw-snackbar--red');
        return;
      }
      
      for (let x = 0; x < this.f6252FinalData.length; x++) {
        if (this.f6252FinalData[x].IS_MULTI == 'Y') {
          this.f6252FinalData[x].Row_data.splice(0, 2);
          
          const finalLines = this.f6252FinalData[x].Row_data.filter((o: any) => o.TRANS_OCCURRENCE == occurrence);
          
          if (type == 'save') {
            if (finalLines && finalLines.length > 0) {
              for (let k = 0; k < finalLines[0].Line_data.length; k++) {
                if (finalLines[0].Line_data[k].EDIT_TYPE == "dropdown" && finalLines[0]['saveEdittedRecord'] == true) {
                  const currentPropDesc = this.formLOV.filter((o: any) => 
                    o.CODE_DESC == finalLines[0].Line_data[k].ATTRIB_VALUE
                  );
                  if (currentPropDesc && currentPropDesc.length > 0) {
                    finalLines[0].Line_data[k].ATTRIB_VALUE = currentPropDesc[0].CODE_NAME;
                  }
                }
                // Always send line 1 details as changed
                finalLines[0].Line_data[k].IS_CHANGED_FLAG = "Y";
                finalData.push(finalLines[0].Line_data[k]);
              }
            }
          } else {
            // If deleted row
            if (finalLines && finalLines.length > 0 && finalLines[0].DELETE_ROWS.length != null && finalLines[0].DELETE_ROWS.length != undefined) {
              for (let j = 0; j < finalLines[0].DELETE_ROWS.length; j++) {
                for (let k = 0; k < finalLines[0].DELETE_ROWS[j].Line_data.length; k++) {
                  if (finalLines[0].DELETE_ROWS[j].Line_data[k].EDIT_TYPE == "dropdown") {
                    const currentPropDesc = this.formLOV.filter((o: any) => 
                      o.CODE_DESC == finalLines[0].DELETE_ROWS[j].Line_data[k].ATTRIB_VALUE
                    );
                    if (currentPropDesc && currentPropDesc.length > 0) {
                      finalLines[0].DELETE_ROWS[j].Line_data[k].ATTRIB_VALUE = currentPropDesc[0].CODE_NAME;
                    }
                  }
                  finalLines[0].DELETE_ROWS[j].Line_data[k].IS_CHANGED_FLAG = "D";
                  finalData.push(finalLines[0].DELETE_ROWS[j].Line_data[k]);
                }
              }
            }
          }
        } else {
          const row_data = this.f6252FinalData[x].Row_data;
          const currentOccSaveData = row_data.filter((obj: any) => obj.TRANS_OCCURRENCE === occurrence);
          
          for (let y = 0; y < currentOccSaveData.length; y++) {
            for (let z = 0; z < currentOccSaveData[y].Line_data.length; z++) {
              if (currentOccSaveData[y].Line_data[z].ROW_TYPE == 'TH' || currentOccSaveData[y].Line_data[z].ROW_TYPE == 'DT') {
                for (let j = 0; j < currentOccSaveData[y].Line_data[z].Line_data_occ.length; j++) {
                  currentOccSaveData[y].Line_data[z].Line_data_occ[j].FORM_INPUT_DATA = 'Y';
                  
                  // line 29a Date
                  if (currentOccSaveData[y].Line_data[z].Line_data_occ[j].EDIT_TYPE == 'custom_date_various') {
                    if (currentOccSaveData[y].Line_data[z].Line_data_occ[j].IS_CHANGED_FLAG == 'Y') {
                      currentOccSaveData[y].Line_data[z].Line_data_occ[j].ATTRIB_VALUE = 
                        this.getDateFormat(currentOccSaveData[y].Line_data[z].Line_data_occ[j].ATTRIB_VALUE);
                    }
                    currentOccSaveData[y].Line_data[z].Line_data_occ[j].FORM_INPUT_DATA = 'Y';
                  }
                  
                  if (type == 'delete') {
                    currentOccSaveData[y].Line_data[z].Line_data_occ[j].IS_CHANGED_FLAG = "D";
                  }
                  
                  finalData.push(currentOccSaveData[y].Line_data[z].Line_data_occ[j]);
                }
              }
            }
          }
        }
      }
      
      clob_settings = [{
        sso_id: this.logged_in_user,
        screen_key: this.globalService.globalParams?.screen_key
      }];
      
      this.sendDetails(finalData, clob_settings, type);
    }
  }

  sendDetails(final_data: any[], clob_settings: any[], type?: string) {
    const message = type === 'delete' 
      ? "Property deleted successfully." 
      : "Form 6252 Details are successfully updated.";
    
    this.crudLoading = true;
    this.isSaveClicked = true;
    
    this.f6252Service.saveForm6252(
      this.GENERAL_CONFIG.base_url,
      final_data,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.deletingOccurrence = null;
        
        // Check for error: callSuccess = "0" OR errorMessage is not "null"
        if (response.callSuccess === "0" || (response.errorMessage && response.errorMessage !== 'null')) {
          const errorMsg = response.errorMessage && response.errorMessage !== 'null' 
            ? response.errorMessage 
            : 'An error occurred while saving.';
          this.gtwSnackbarService.openSnackBar(errorMsg, 'gtw-snackbar--red');
        } else {
          // Success: callSuccess = "1" and errorMessage = "null"
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.deletingOccurrence = null;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        console.error('Save error:', error);
        this.cdr.markForCheck();
      }
    );
  }

  getDateFormat(dateValue: any): string {
    if (dateValue == "VARIOUS" || dateValue == null) {
      return dateValue;
    }
    
    // If already string in MM/DD/YYYY format
    if (typeof dateValue === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateValue)) {
      return dateValue;
    }
    
    const date = new Date(dateValue);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  }

  updateFormDataValues() {
    for (let i = 0; i < this.f6252FinalData.length; i++) {
      if (this.f6252FinalData[i].IS_MULTI == 'Y') {
        for (let j = 0; j < this.f6252FinalData[i].Row_data.length; j++) {
          if (this.f6252FinalData[i].Row_data[j].Line_Type == "NEW_EDIT_ROW") {
            for (let l = 0; l < this.f6252FinalData[i].Row_data[j].Line_data.length; l++) {
              if (this.f6252FinalData[i].Row_data[j].Line_data[l].IS_EDITABLE_CELL == "Y" && 
                  this.f6252FinalData[i].Row_data[j].Line_data[l].DATA_TYPE == "dateVarious" && 
                  this.f6252FinalData[i].Row_data[j].Line_data[l].EDIT_TYPE == "custom_date_various") {
                this.f6252FinalData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = 
                  this.getDateFormat(this.f6252FinalData[i].Row_data[j].Line_data[l].ATTRIB_VALUE);
              } else if (this.f6252FinalData[i].Row_data[j].Line_data[l].IS_EDITABLE_CELL == "Y" && 
                         this.f6252FinalData[i].Row_data[j].Line_data[l].DATA_TYPE == "string" && 
                         this.f6252FinalData[i].Row_data[j].Line_data[l].EDIT_TYPE == "checkbox") {
                if (this.f6252FinalData[i].Row_data[j].Line_data[l].ATTRIB_VALUE == null || 
                    this.f6252FinalData[i].Row_data[j].Line_data[l].ATTRIB_VALUE == undefined) {
                  this.f6252FinalData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = "N";
                }
              }
            }
          }
        }
      }
    }
  }

  validateFormValues(currentOccurrence: number): number {
    let returnValue = 0;
    let dateSold: any = null;
    let dateAcquired: any = null;
    let propertyExists = false;
    
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          const line_no = this.f6252FormData[i].Row_data[j].Line_no;
          
          // Check if property is already assigned
          const currentProperty = this.f6252FormData[i].Row_data.filter((o: any) => 
            o.TRANS_OCCURRENCE == currentOccurrence
          );
          
          if ((this.f6252FormData[i].Row_data[j].Line_Type == "DATA_ROW" || 
               this.f6252FormData[i].Row_data[j].Line_Type == "NEW_EDIT_ROW") && 
              this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE != currentOccurrence) {
            const property_desc_index1 = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "PROPERTY_DESC_" + line_no
            );
            
            if (property_desc_index1 >= 0) {
              const selectedVal = this.formLOV.filter((o: any) => 
                o.CODE_DESC == this.f6252RowData[i].Row_data[j].Line_data[property_desc_index1].ATTRIB_VALUE
              );
              
              if (selectedVal.length > 0 && currentProperty.length > 0 && 
                  selectedVal[0].CODE_NAME == currentProperty[0].Line_data[1].ATTRIB_VALUE) {
                propertyExists = true;
              }
            }
          }
          
          if (this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == currentOccurrence) {
            if (this.f6252FormData[i].Row_data[j].Line_Type == "DATA_ROW" || 
                this.f6252FormData[i].Row_data[j].Line_Type == "NEW_EDIT_ROW") {
              const property_desc_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                x.ATTRIB_SHORT_CODE == "PROPERTY_DESC_" + line_no
              );
              const user_desc_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                x.ATTRIB_SHORT_CODE == "DESCRIPTION_" + line_no
              );
              const date_acquired_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                x.ATTRIB_SHORT_CODE == "DATE_ACQ_" + line_no
              );
              const date_sold_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                x.ATTRIB_SHORT_CODE == "DATE_SOLD_" + line_no
              );
              const long_short_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                x.ATTRIB_SHORT_CODE == "LONG_SHORT_TERM_" + line_no
              );
              const related_party_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                x.ATTRIB_SHORT_CODE == "RELATED_PARTY_" + line_no
              );
              const marketable_security_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                x.ATTRIB_SHORT_CODE == "MARKETABLE_SECURITY_" + line_no
              );
              
              let selling_price_index = -1;
              if (this.curentyear == '2024') {
                selling_price_index = this.f6252FormData[i].Row_data[j].Line_data.findIndex((x: any) => 
                  x.ATTRIB_SHORT_CODE == "SELLING_PRICE_" + line_no
                );
              }
              
              if (propertyExists && property_desc_index >= 0) {
                this.f6252FormData[i].Row_data[j].Line_data[property_desc_index].HAS_ERROR = "Y";
                this.f6252FormData[i].Row_data[j].Line_data[property_desc_index].ATTRIB_VALUE_ERROR = "Property exists, please select a different property.";
                returnValue = 1;
              }
              
              if (property_desc_index >= 0 && 
                  (this.f6252FormData[i].Row_data[j].Line_data[property_desc_index].ATTRIB_VALUE == null || 
                   this.f6252FormData[i].Row_data[j].Line_data[property_desc_index].ATTRIB_VALUE == undefined || 
                   this.f6252FormData[i].Row_data[j].Line_data[property_desc_index].ATTRIB_VALUE.length <= 0)) {
                this.f6252FormData[i].Row_data[j].Line_data[property_desc_index].HAS_ERROR = "Y";
                this.f6252FormData[i].Row_data[j].Line_data[property_desc_index].ATTRIB_VALUE_ERROR = "Enter Description of Property.";
                returnValue = 1;
              }
              
              // User description
              if (user_desc_index >= 0 && 
                  (this.f6252FormData[i].Row_data[j].Line_data[user_desc_index].ATTRIB_VALUE == null || 
                   this.f6252FormData[i].Row_data[j].Line_data[user_desc_index].ATTRIB_VALUE == undefined || 
                   this.f6252FormData[i].Row_data[j].Line_data[user_desc_index].ATTRIB_VALUE.length <= 0)) {
                this.f6252FormData[i].Row_data[j].Line_data[user_desc_index].HAS_ERROR = "Y";
                this.f6252FormData[i].Row_data[j].Line_data[user_desc_index].ATTRIB_VALUE_ERROR = "Enter User Description.";
                returnValue = 1;
              }
              
              // Date acquired validation
              if (date_acquired_index >= 0 && 
                  this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE != "VARIOUS" && 
                  this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE != null) {
                const acquiredVal = this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE;
                
                if (typeof acquiredVal === 'object') {
                  const acquiredDate = new Date(acquiredVal);
                  if (acquiredDate.getFullYear() > parseInt(this.globalService.globalParams.tax_year)) {
                    this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].HAS_ERROR = "Y";
                    this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE_ERROR = "The Date Acquired year has to be current tax year or prior year.";
                    returnValue = 1;
                  }
                } else if (typeof acquiredVal === 'string') {
                  const yearMatch = acquiredVal.match(/\d{4}$/);
                  if (yearMatch && parseInt(yearMatch[0]) > parseInt(this.globalService.globalParams.tax_year)) {
                    this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].HAS_ERROR = "Y";
                    this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE_ERROR = "The Date Acquired year has to be current tax year or prior year.";
                    returnValue = 1;
                  }
                }
              }
              
              // Date sold validation
              if (date_sold_index >= 0 && 
                  this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].ATTRIB_VALUE != "VARIOUS") {
                const soldVal = this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].ATTRIB_VALUE;
                
                if (soldVal == null) {
                  this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].HAS_ERROR = "Y";
                  this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].ATTRIB_VALUE_ERROR = "Select Date Sold.";
                  returnValue = 1;
                } else {
                  if (typeof soldVal === 'object') {
                    const soldDate = new Date(soldVal);
                    if (soldDate.getFullYear() > parseInt(this.globalService.globalParams.tax_year)) {
                      this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].HAS_ERROR = "Y";
                      this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].ATTRIB_VALUE_ERROR = "The Date Sold year has to be same as tax year.";
                      returnValue = 1;
                    }
                  } else if (typeof soldVal === 'string') {
                    const yearMatch = soldVal.match(/\d{4}$/);
                    if (yearMatch && parseInt(yearMatch[0]) > parseInt(this.globalService.globalParams.tax_year)) {
                      this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].HAS_ERROR = "Y";
                      this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].ATTRIB_VALUE_ERROR = "The Date Sold year has to be same as tax year.";
                      returnValue = 1;
                    }
                  }
                }
              }
              
              // Compare dates
              if (date_sold_index >= 0 && date_acquired_index >= 0 && 
                  this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].ATTRIB_VALUE != "VARIOUS" && 
                  this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE != "VARIOUS" && 
                  this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE != null) {
                const date_acquired_parsed = Date.parse(this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE);
                const date_sold_parsed = Date.parse(this.f6252FormData[i].Row_data[j].Line_data[date_sold_index].ATTRIB_VALUE);
                
                if (date_acquired_parsed > date_sold_parsed) {
                  this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].HAS_ERROR = "Y";
                  this.f6252FormData[i].Row_data[j].Line_data[date_acquired_index].ATTRIB_VALUE_ERROR = "The Date Acquired should be prior to Date Sold";
                  returnValue = 1;
                }
              }
              
              // Long/Short Term validation
              if (long_short_index >= 0 && this.f6252FormData[i].Row_data[j].Line_data[long_short_index].ATTRIB_VALUE == null) {
                this.f6252FormData[i].Row_data[j].Line_data[long_short_index].HAS_ERROR = "Y";
                this.f6252FormData[i].Row_data[j].Line_data[long_short_index].ATTRIB_VALUE_ERROR = "Please select Long/Short Term.";
                returnValue = 1;
              }
              
              // Related party validation
              if (related_party_index >= 0 && this.f6252FormData[i].Row_data[j].Line_data[related_party_index].ATTRIB_VALUE == null) {
                this.f6252FormData[i].Row_data[j].Line_data[related_party_index].HAS_ERROR = "Y";
                this.f6252FormData[i].Row_data[j].Line_data[related_party_index].ATTRIB_VALUE_ERROR = "Please select Property sold to a related party after May 14, 1980?";
                returnValue = 1;
              }
              
              // Marketable security validation
              if (marketable_security_index >= 0 && this.f6252FormData[i].Row_data[j].Line_data[marketable_security_index].ATTRIB_VALUE == null) {
                this.f6252FormData[i].Row_data[j].Line_data[marketable_security_index].HAS_ERROR = "Y";
                this.f6252FormData[i].Row_data[j].Line_data[marketable_security_index].ATTRIB_VALUE_ERROR = "Please select Property you sold to a related party a marketable security?";
                returnValue = 1;
              }
              
              // Selling price validation (2024 only)
              if (this.curentyear == '2024' && selling_price_index >= 0 && 
                  this.f6252FormData[i].Row_data[j].Line_data[selling_price_index].ATTRIB_VALUE == null) {
                this.f6252FormData[i].Row_data[j].Line_data[selling_price_index].HAS_ERROR = "Y";
                this.f6252FormData[i].Row_data[j].Line_data[selling_price_index].ATTRIB_VALUE_ERROR = "Please select total selling price to be determined?";
                returnValue = 1;
              }
            }
          }
        }
      }
    }
    
    return returnValue;
  }

  updateFilteredOuterList() {
    this.filteredOuterList = this.outerList.filter((o: any) => 
      o.IS_FORM_DISPLAY === 'Y' && o.TRANS_OCCURRENCE == this.currentOccurrence
    );
  }

  AddNewRow(occurrence: any, line_no: any, index: any) {
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        
        // Can't create more than 4 properties
        const propertyLines = this.f6252FormData[i].Row_data.filter((o: any) => o.Line_Type == "INITIAL_ROW");
        
        if (propertyLines.length == 0) {
          this.gtwSnackbarService.openSnackBar("All properties are utilized. Can't add a new property.", 'gtw-snackbar--red');
          return;
        }
        
        // To save existing Property before we create new one
        const totalLines = this.f6252FormData[i].Row_data.filter((o: any) => 
          o.IS_EDITTED == "Y" || o.Line_Type == "NEW_EDIT_ROW"
        );
        
        if (totalLines.length > 0) {
          this.gtwSnackbarService.openSnackBar("Please save the details of property " + totalLines[0].Line_data[1].ATTRIB_VALUE + " before you create a new property", 'gtw-snackbar--red');
          return;
        }
        
        // Get trans_details_key from last row (like original AngularJS)
        let trans_details_key: any = null;
        const lastRowIndex = this.f6252FormData[i].Row_data.length - 1;
        if (lastRowIndex >= 0 && 
            this.f6252FormData[i].Row_data[lastRowIndex].Trans_details_key != null) {
          trans_details_key = this.f6252FormData[i].Row_data[lastRowIndex].Trans_details_key;
        }
        
        let new_row: any;
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          if (this.f6252FormData[i].Row_data[j].Line_Type == 'INITIAL_ROW') {
            new_row = this.f6252FormData[i].Row_data[j];
            new_row.Line_Type = "NEW_EDIT_ROW";
            new_row['saveEdittedRecord'] = false;
            break;
          }
        }
        
        if (new_row && new_row.Line_no == line_no) {
          for (let j = 0; j < new_row.Line_data.length; j++) {
            if (new_row.Line_data[j].EDIT_TYPE == "custom_date_various" && new_row.Line_data[j].DATA_TYPE == "dateVarious") {
              new_row.Line_data[j].ATTRIB_VALUE = null;
            } else if (new_row.Line_data[j].EDIT_TYPE == "currency" && new_row.Line_data[j].DATA_TYPE == "number") {
              new_row.Line_data[j].ATTRIB_VALUE = 0;
            } else if (new_row.Line_data[j].EDIT_TYPE == "label" && new_row.Line_data[j].DATA_TYPE == "string") {
              // Keep label value
            } else {
              new_row.Line_data[j].ATTRIB_VALUE = null;
            }
            // Add TRANS_DETAILS_KEY to each line_data (like original AngularJS)
            new_row.Line_data[j].TRANS_DETAILS_KEY = trans_details_key;
            new_row.Line_data[j].IS_CHANGED_FLAG = "Y";
          }
          this.currentOccurrence = new_row.TRANS_OCCURRENCE;
        }
      }
    }
    this.showAttributes = true;
    this.updateFilteredOuterList();
    this.cdr.detectChanges();
  }

  editExistingRecord(line_no: any, occurrence: any, index: any, type: any) {
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          if (j == index) {
            
            if (this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == occurrence) {
              let editLines: any[] = [];
              if (this.f6252FormData[i].Row_data[j].IS_CURRENT_CHANGED_ROW == "Y") {
                // DO NOTHING
              } else {
                editLines = this.f6252FormData[i].Row_data.filter((o: any) => 
                  (o.IS_EDITTED == "Y" && o.IS_CURRENT_CHANGED_ROW == "Y") || o.Line_Type == "NEW_EDIT_ROW"
                );
              }
              
              if (editLines.length > 0) {
                this.gtwSnackbarService.openSnackBar("Please save the details of the changed property before you Edit a property", 'gtw-snackbar--red');
                return;
              }
            }
            
            if (this.f6252FormData[i].Row_data[j].Line_Type == "DATA_ROW" && 
                this.f6252FormData[i].Row_data[j].Line_no == line_no && 
                this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == occurrence) {
              this.f6252FormData[i].Row_data[j].Line_Type = "NEW_EDIT_ROW";
              
              for (let l = 0; l < this.f6252FormData[i].Row_data[j].Line_data.length; l++) {
                if (this.f6252FormData[i].Row_data[j].Line_data[l].EDIT_TYPE == "currency" && 
                    this.f6252FormData[i].Row_data[j].Line_data[l].DATA_TYPE == "number") {
                  this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = 
                    parseInt(this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE);
                } else if (this.f6252FormData[i].Row_data[j].Line_data[l].EDIT_TYPE == "custom_date_various" && 
                           this.f6252FormData[i].Row_data[j].Line_data[l].DATA_TYPE == "dateVarious") {
                  if (this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE != "VARIOUS" && 
                      this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE != null) {
                    this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = 
                      new Date(this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE);
                  }
                  // Add isOpen flag like original AngularJS
                  this.f6252FormData[i].Row_data[j].Line_data[l]['isOpen'] = false;
                } else if (this.f6252FormData[i].Row_data[j].Line_data[l].EDIT_TYPE == "dropdown") {
                  const currentProp = this.formLOV.filter((o: any) => 
                    o.CODE_DESC == this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE
                  );
                  if (currentProp && currentProp.length > 0) {
                    this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = currentProp[0].CODE_NAME;
                  }
                }
              }
            }
          }
        }
      }
    }
    this.currentOccurrence = occurrence;
    this.showAttributes = true;
    this.updateFilteredOuterList();
    this.cdr.detectChanges();
  }

  removeExistingRecord(line_no: any, Occurrence: any, index: any) {
    // FIRST: Check for warnings (like AngularJS)
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        
        const editLines = this.f6252FormData[i].Row_data.filter((o: any) => 
          o.IS_EDITTED == "Y" && o.TRANS_OCCURRENCE != Occurrence
        );
        
        if (editLines.length > 0) {
          if (editLines[0].Line_Type == "DATA_ROW") {
            this.gtwSnackbarService.openSnackBar("Please save the details of the changed property " + editLines[0].Line_data[1].ATTRIB_VALUE + " before you delete a property", 'gtw-snackbar--red');
          } else if (editLines[0].Line_Type == "NEW_EDIT_ROW") {
            const editedProp = this.formLOV.filter((o: any) => 
              o.CODE_NAME == editLines[0].Line_data[1].ATTRIB_VALUE
            );
            if (editedProp && editedProp.length > 0) {
              this.gtwSnackbarService.openSnackBar("Please save the details of the changed property " + editedProp[0].CODE_DESC + " before you delete a property", 'gtw-snackbar--red');
            }
          }
          return; // Exit if there are warnings
        }
        
        // SECOND: Show confirm dialog (after warnings check)
        const confirmed = window.confirm('Are you sure you want to permanently delete this Property?');
        
        if (!confirmed) {
          return;
        }
      }
    }
    
    // THIRD: Proceed with deletion
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          if (j == index) {
            if ((this.f6252FormData[i].Row_data[j].Line_Type == "DATA_ROW" || 
                 this.f6252FormData[i].Row_data[j].Line_Type == "NEW_EDIT_ROW") && 
                this.f6252FormData[i].Row_data[j].Line_no == line_no && 
                null != this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE && 
                this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE != "" && 
                this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == Occurrence) {
              
              // Never saved in to DB
              if (this.f6252FormData[i].Row_data[j].Line_Type == "NEW_EDIT_ROW" && 
                  this.f6252FormData[i].Row_data[j].FORM_INPUT_DATA == 'N') {
                this.f6252FormData[i].Row_data[j].Line_Type = 'INITIAL_ROW';
                this.f6252FormData[i].Row_data[j].IS_EDITTED = 'N';
                this.f6252FormData[i].Row_data[j].DELETE_ROWS = [];
                this.showAttributes = false;
                
                for (let l = 0; l < this.f6252FormData[i].Row_data[j].Line_data.length; l++) {
                  this.f6252FormData[i].Row_data[j].Line_data[l].HAS_ERROR = 'N';
                }
                
                // Assign 0 and null values like new row
                const deleteValues = this.outerList.filter((o: any) => o.TRANS_OCCURRENCE == Occurrence);
                
                for (let m = 0; m < deleteValues.length; m++) {
                  if (deleteValues[m].EDIT_TYPE == "custom_date_various" && deleteValues[m].DATA_TYPE == "dateVarious") {
                    deleteValues[m].ATTRIB_VALUE = null;
                  } else if (deleteValues[m].EDIT_TYPE == "currency" && deleteValues[m].DATA_TYPE == "number") {
                    deleteValues[m].ATTRIB_VALUE = 0;
                  } else if (deleteValues[m].EDIT_TYPE == "label" && deleteValues[m].DATA_TYPE == "string") {
                    // Keep label
                  } else {
                    deleteValues[m].ATTRIB_VALUE = null;
                  }
                }
                
                // Update view after resetting values
                this.updateFilteredOuterList();
              } else {
                // Already saved in DB - mark for deletion and save
                this.f6252FormData[i].Row_data[j].DELETE_ROWS.push(this.f6252FormData[i].Row_data[j]);
                this.currentOccurrence = Occurrence;
                this.deletingOccurrence = Occurrence;
                // Show loading message before save
                this.gtwSnackbarService.openSnackBar("Deleting property...", 'gtw-snackbar--yellow');
                this.save(this.currentOccurrence, 'delete');
              }
            }
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  saveNewEdittedRecord(line_no: any, occurrence: any, index: any) {
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          if (j == index) {
            
            // To fill the required fields
            const returnValue = this.validateFormValues(occurrence);
            if (returnValue == 1) {
              return;
            }
            
            if (this.f6252FormData[i].Row_data[j].Line_Type == "NEW_EDIT_ROW" && 
                this.f6252FormData[i].Row_data[j].Line_no == line_no && 
                this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == occurrence) {
              this.f6252FormData[i].Row_data[j].Line_Type = "DATA_ROW";
              this.f6252FormData[i].Row_data[j]['saveEdittedRecord'] = true;
              
              if (this.f6252FormData[i].Row_data[j].IS_EDITTED == "Y") {
                // This flag is to know which row is currently changed and we can edit that row multiple times
                this.f6252FormData[i].Row_data[j].IS_CURRENT_CHANGED_ROW = "Y";
              }
            }
            
            for (let l = 0; l < this.f6252FormData[i].Row_data[j].Line_data.length; l++) {
              if (this.f6252FormData[i].Row_data[j].Line_data[l].IS_EDITABLE_CELL == "Y" && 
                  this.f6252FormData[i].Row_data[j].Line_data[l].DATA_TYPE == "dateVarious" && 
                  this.f6252FormData[i].Row_data[j].Line_data[l].EDIT_TYPE == "custom_date_various") {
                this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = 
                  this.getDateFormat(this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE);
              } else if (this.f6252FormData[i].Row_data[j].Line_data[l].IS_EDITABLE_CELL == "Y" && 
                         this.f6252FormData[i].Row_data[j].Line_data[l].DATA_TYPE == "string" && 
                         this.f6252FormData[i].Row_data[j].Line_data[l].EDIT_TYPE == "checkbox") {
                if (this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE == null || 
                    this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE == undefined) {
                  this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = "N";
                }
              } else if (this.f6252FormData[i].Row_data[j].Line_data[l].IS_EDITABLE_CELL == "Y" && 
                         this.f6252FormData[i].Row_data[j].Line_data[l].EDIT_TYPE == "dropdown") {
                const currentProp = this.formLOV.filter((o: any) => 
                  o.CODE_NAME == this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE
                );
                if (currentProp && currentProp.length > 0) {
                  this.f6252FormData[i].Row_data[j].Line_data[l].ATTRIB_VALUE = currentProp[0].CODE_DESC;
                }
              }
            }
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  valuechanged(parent_index: number, occurrence: any, line_no: any, index: any) {
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        
        const totalLines = this.f6252FormData[i].Row_data.filter((o: any) => o.Line_Type == "NEW_EDIT_ROW");
        
        if (this.f6252FormData[i].Row_data[parent_index] != null && 
            this.f6252FormData[i].Row_data[parent_index] != undefined) {
          if (this.f6252FormData[i].Row_data[parent_index].Line_no == line_no && 
              this.f6252FormData[i].Row_data[parent_index].TRANS_OCCURRENCE == occurrence) {
            
            // To Default select Sch D / F6252 on Date sold
            if (this.f6252FormData[i].Row_data[parent_index].Line_data[index + 1].ATTRIB_DESCRIPTION == "Date sold (mm/dd/yyyy)") {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              
              const scheduleLine = totalLines[0].Line_data.filter((o: any) => o.ATTRIB_DESCRIPTION == 'Schedule');
              
              if (this.f6252FormData[i].Row_data[parent_index].Line_data[index + 1].ATTRIB_VALUE > today) {
                if (scheduleLine && scheduleLine.length > 0) {
                  scheduleLine[0].ATTRIB_VALUE = 'D';
                }
              } else {
                if (scheduleLine && scheduleLine.length > 0) {
                  scheduleLine[0].ATTRIB_VALUE = '4797';
                }
              }
            }
            
            this.f6252FormData[i].Row_data[parent_index].Line_data[index + 1].IS_CHANGED_FLAG = "Y";
            this.f6252FormData[i].Row_data[parent_index].Line_data[index + 1].HAS_ERROR = "N";
          }
        }
        
        // To know if any obj under that trans occ list is changed
        if (totalLines && totalLines.length > 0) {
          totalLines[0].IS_EDITTED = "Y";
        }
      } else {
        if (this.f6252FormData[i].IS_MULTI == 'N') {
          const outerListLine = this.outerList.filter((o: any) => 
            o.LINE_NO == line_no && o.TRANS_OCCURRENCE == occurrence
          );
          
          if (outerListLine.length != 0) {
            if (outerListLine[0].LINE_NO == line_no && outerListLine[0] != undefined && outerListLine[0] != null) {
              outerListLine[0].IS_CHANGED_FLAG = "Y";
              
              if (outerListLine[0].LINE_NO == '29(a)') {
                const line29aDate = this.outerList.filter((o: any) => 
                  o.LINE_NO == '29(a)(ii)' && o.TRANS_OCCURRENCE == occurrence
                );
                
                if (outerListLine[0].ATTRIB_VALUE == 'true') {
                  outerListLine[0]['conditionalField'] = false;
                  if (outerListLine[0]['line29DateVal'] != '0' && outerListLine[0]['line29DateVal'] != undefined) {
                    if (line29aDate && line29aDate.length > 0) {
                      line29aDate[0].ATTRIB_VALUE = outerListLine[0]['line29DateVal'];
                      line29aDate[0].IS_CHANGED_FLAG = 'Y';
                    }
                  }
                } else {
                  if (line29aDate && line29aDate.length > 0) {
                    line29aDate[0].ATTRIB_VALUE = null;
                  }
                  outerListLine[0]['line29DateVal'] = null;
                  outerListLine[0]['conditionalField'] = true;
                }
              }
            }
          }
        }
      }
    }
  }

  updateAmount(index: number, occurence: any, line_no: any) {
    this.calcFinalAmounts(occurence);
  }

  calcFinalAmounts(occurence: any) {
    // FIRST PASS: Calculate hidden attributes (IS_FORM_DISPLAY='N') to ensure they're available for dependent formulas
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'N') {
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          if (this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == occurence) {
            for (let m = 0; m < this.f6252FormData[i].Row_data[j].Line_data.length; m++) {
              for (let n = 0; n < this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ.length; n++) {
                const item = this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n];
                if (item.ATTRIB_CALC_FORMULA != null && item.IS_FORM_DISPLAY == 'N') {
                  item.ATTRIB_VALUE = this.calc_final_amt(item.ATTRIB_CALC_FORMULA, occurence);
                }
              }
            }
          }
        }
      }
    }
    
    // SECOND PASS: Calculate visible attributes (IS_FORM_DISPLAY='Y') which may depend on hidden attributes
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'N') {
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          if (this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == occurence) {
            for (let m = 0; m < this.f6252FormData[i].Row_data[j].Line_data.length; m++) {
              for (let n = 0; n < this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ.length; n++) {
                const item = this.f6252FormData[i].Row_data[j].Line_data[m].Line_data_occ[n];
                if (item.ATTRIB_CALC_FORMULA != null && item.IS_FORM_DISPLAY == 'Y') {
                  item.ATTRIB_VALUE = this.calc_final_amt(item.ATTRIB_CALC_FORMULA, occurence);
                  item.IS_CHANGED_FLAG = "Y";
                }
              }
            }
          }
        }
      }
    }
    
    // Update filteredOuterList if this is the current occurrence
    if (occurence == this.currentOccurrence && this.showAttributes) {
      this.updateFilteredOuterList();
      this.cdr.detectChanges();
    }
  }

  calc_final_amt(formula: string, occurence: any): any {
    // EXACTLY replicate AngularJS logic from F-6252Ctrl.js lines 1098-1193
    
    // Extract variable names from formula
    const matches = formula.match(/\b[^\d\s]+\b/g);
    let lineFormulaArray: string[] = [];
    
    if (matches == null) {
      lineFormulaArray = formula.split(/[+-/\\*\\]/);
    } else {
      // CRITICAL: Extract substring from first '(' to second-to-last char
      const formulaLength = formula.length;
      const subFormula = formula.substring(formula.indexOf("("), formulaLength - 1);
      // CRITICAL: Split by +, -, comma, /, * (matches AngularJS line 1108)
      lineFormulaArray = subFormula.split(/[+-,/\\*\\]/);
    }
    
    // Replace each variable with its value
    for (let i = 0; i < lineFormulaArray.length; i++) {
      let lineAmt: any = 0;
      
      // CRITICAL: Only process if element has parentheses (matches AngularJS line 1113)
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        // CRITICAL: Remove quotes and ALL parentheses from variable name (matches AngularJS line 1114)
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        
        for (let k = 0; k < this.f6252FormData.length; k++) {
          if (this.f6252FormData[k].IS_MULTI == 'Y') {
            // Handle multi-row data (sum all occurrences)
            for (let j = 0; j < this.f6252FormData[k].Row_data.length; j++) {
              if (this.f6252FormData[k].Row_data[j].Line_Type == "DATA_ROW" || 
                  this.f6252FormData[k].Row_data[j].Line_Type == "NEW_EDIT_ROW") {
                const row_data = this.f6252FormData[k].Row_data[j].Line_data;
                const currentTabAttribVal = row_data.filter((obj: any) => 
                  obj.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
                );
                
                if (currentTabAttribVal != null && currentTabAttribVal.length > 0) {
                  lineAmt = lineAmt + parseInt(currentTabAttribVal[0].ATTRIB_VALUE || 0);
                }
              }
            }
          } else {
            // Handle single occurrence data
            const row_data = this.f6252FormData[k].Row_data;
            const currentOccData = row_data.filter((obj: any) => obj.TRANS_OCCURRENCE === occurence);
            
            if (currentOccData && currentOccData.length > 0) {
              for (let a = 0; a < currentOccData[0].Line_data.length; a++) {
                for (let b = 0; b < currentOccData[0].Line_data[a].Line_data_occ.length; b++) {
                  if (currentOccData[0].Line_data[a].Line_data_occ[b].ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()) {
                    lineAmt = currentOccData[0].Line_data[a].Line_data_occ[b].ATTRIB_VALUE;
                    
                    // CRITICAL: Replace (VAR_NAME) with value - exactly like AngularJS lines 1140-1144
                    if (parseFloat(lineAmt) >= 0) {
                      formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt);
                    } else {
                      formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt + ")");
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    // Evaluate the formula
    if (formula.substring(0, 7) == 'toFixed') {
      // CRITICAL: Handle toFixed formula - exactly like AngularJS lines 1167-1179
      const toFixedLength = 'toFixed';
      const charLength = toFixedLength.length;
      const formulaLength = formula.length;
      const str = formula.slice(charLength, formulaLength);
      
      // CRITICAL: Split by comma and * ONLY (NOT by /) - matches AngularJS line 1173
      const formulaParts = str.split(/[,\\*\\]/);
      
      // CRITICAL: Remove quotes and ALL parentheses - matches AngularJS lines 1174-1175
      formulaParts[0] = formulaParts[0].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
      formulaParts[1] = formulaParts[1].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
      
      // CRITICAL: Eval first part and apply toFixed with second part - matches AngularJS lines 1177-1178
      const calculatedVal = safeEvalFormula(formulaParts[0], 0, 'f-6252.toFixedFormula');
      return (calculatedVal ?? 0).toFixed(parseInt(formulaParts[1]) || 0);

    } else {
      // Handle regular formula - exactly like AngularJS lines 1181-1185
      const calculatedVal = safeEvalFormula(formula, null, 'f-6252.regularFormula');
      if (calculatedVal != null && parseInt(String(calculatedVal)) === parseInt(String(calculatedVal))) {
        return calculatedVal.toFixed(0);
      } else {
        return 0;
      }
    }
  }

  convertToDateInputFormat(dateValue: any): string {
    if (!dateValue || dateValue === 'VARIOUS' || dateValue === '') {
      return '';
    }
    
    // If it's a Date object, convert to YYYY-MM-DD
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = ('0' + (dateValue.getMonth() + 1)).slice(-2);
      const day = ('0' + dateValue.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    }
    
    // If it's a string
    if (typeof dateValue === 'string') {
      // Check if already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      // Parse MM/DD/YYYY format
      const parts = dateValue.split('/');
      if (parts.length === 3) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    }
    
    return '';
  }

  onDateChange(dateStr: string, row: any) {
    if (!dateStr) {
      row.ATTRIB_VALUE = '';
      return;
    }
    
    // Convert YYYY-MM-DD to MM/DD/YYYY
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      row.ATTRIB_VALUE = `${month}/${day}/${year}`;
      
      // Call valuechanged to mark as changed
      row.IS_CHANGED_FLAG = 'Y';
      
      // Clear validation error when value is corrected
      row.HAS_ERROR = 'N';
      row.ATTRIB_VALUE_ERROR = '';
    }
  }

  onLine29aDateChange(dateStr: string, row: any, idx: number) {
    if (!dateStr) {
      row.line29DateVal = null;
      return;
    }
    
    // Store as Date object (like original AngularJS)
    // HTML date input gives YYYY-MM-DD format
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // JS months are 0-indexed
      const day = parseInt(parts[2]);
      row.line29DateVal = new Date(year, month, day);
      row['isOpen'] = false;
      
      // Call valuechanged to update line 29(a)(ii) value
      const parentIndex = this.filteredOuterList.findIndex((r: any) => r === row);
      if (parentIndex !== -1) {
        this.valuechanged(parentIndex, row.TRANS_OCCURRENCE, row.LINE_NO, idx);
      }
    }
  }

  onCheckboxChange(event: Event, row: any) {
    const target = event.target as HTMLInputElement;
    row.ATTRIB_VALUE = target.checked ? 'true' : 'false';
    row.IS_CHANGED_FLAG = 'Y';
  }

  onRadioCheckboxChange(event: Event, rowData: any) {
    const target = event.target as HTMLInputElement;
    rowData.ATTRIB_VALUE = target.checked ? 'true' : 'false';
    this.checkBoxHandler(rowData.FORM_LINE_KEY, rowData.LINE_ATTRIB_KEY, rowData.ATTRIB_COL, rowData.TRANS_OCCURRENCE);
  }

  isAnyRowInEditMode(): boolean {
    // Check if any row is currently in edit mode (NEW_EDIT_ROW)
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        const editRows = this.f6252FormData[i].Row_data.filter((row: any) => row.Line_Type === 'NEW_EDIT_ROW');
        if (editRows.length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  cancelEdit(line_no: any, occurrence: any, index: any) {
    // Revert the row from NEW_EDIT_ROW back to DATA_ROW or INITIAL_ROW
    for (let i = 0; i < this.f6252FormData.length; i++) {
      if (this.f6252FormData[i].IS_MULTI == 'Y') {
        for (let j = 0; j < this.f6252FormData[i].Row_data.length; j++) {
          if (j == index && 
              this.f6252FormData[i].Row_data[j].Line_Type == "NEW_EDIT_ROW" && 
              this.f6252FormData[i].Row_data[j].Line_no == line_no && 
              this.f6252FormData[i].Row_data[j].TRANS_OCCURRENCE == occurrence) {
            
            // If this was a new row being added (FORM_INPUT_DATA='N'), revert to INITIAL_ROW
            if (this.f6252FormData[i].Row_data[j].FORM_INPUT_DATA == 'N') {
              this.f6252FormData[i].Row_data[j].Line_Type = 'INITIAL_ROW';
              this.f6252FormData[i].Row_data[j].IS_EDITTED = 'N';
              this.showAttributes = false;
              
              // Reset all values to defaults
              const resetValues = this.outerList.filter((o: any) => o.TRANS_OCCURRENCE == occurrence);
              for (let m = 0; m < resetValues.length; m++) {
                if (resetValues[m].EDIT_TYPE == "custom_date_various" && resetValues[m].DATA_TYPE == "dateVarious") {
                  resetValues[m].ATTRIB_VALUE = null;
                } else if (resetValues[m].EDIT_TYPE == "currency" && resetValues[m].DATA_TYPE == "number") {
                  resetValues[m].ATTRIB_VALUE = 0;
                } else if (resetValues[m].EDIT_TYPE == "label" && resetValues[m].DATA_TYPE == "string") {
                  // Keep label
                } else {
                  resetValues[m].ATTRIB_VALUE = null;
                }
              }
            } else {
              // If this was an existing row being edited, revert to DATA_ROW
              // Reload original data from backup
              this.f6252FormData[i].Row_data[j].Line_Type = 'DATA_ROW';
              this.f6252FormData[i].Row_data[j].IS_EDITTED = 'N';
              
              // Restore original values from f6252OriginalData
              const originalRow = this.f6252OriginalData[i].Row_data[j];
              if (originalRow) {
                this.f6252FormData[i].Row_data[j].Line_data = _.cloneDeep(originalRow.Line_data);
              }
            }
            
            this.updateFilteredOuterList();
            this.cdr.detectChanges();
            break;
          }
        }
      }
    }
  }

  cancel() {
    if (_.isEqual(this.f6252FormData, this.f6252OriginalData)) {
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this.gtwSnackbarService.openSnackBar("The transaction is still in progress", 'gtw-snackbar--yellow');
      } else {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }
}

